import { useEffect, useState, useCallback, useRef } from "react";
import { Coins, Printer, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";
import { SignatureImage, printElement } from "../components/SignaturePad";
import PayslipPrint from "../components/PayslipPrint";

const money = (n) =>
  `₡ ${Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Row({ label, value, bold, neg }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={`text-xs ${bold ? "font-bold text-zinc-900" : "text-zinc-600"}`}>{label}</span>
      <span className={`text-xs font-mono ${bold ? "font-bold text-zinc-900" : neg ? "text-red-600" : "text-zinc-800"}`}>
        {value}
      </span>
    </div>
  );
}

export default function EmployeeSalariesTab({ employeeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState(null);
  const [period, setPeriod] = useState(null);
  const [opening, setOpening] = useState(false);
  const receiptRef = useRef(null);
  const payslipRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/employee/${employeeId}/salary-history`);
      setRows(res.data || []);
    } catch {
      toast.error("Error al cargar el historial de salarios");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId) load();
  }, [employeeId, load]);

  const openReceipt = async (r) => {
    setOpening(true);
    try {
      const res = await api.get(`/payroll/periods/${r.period_id}/entries/${r.entry_id}`);
      setEntry(res.data);
      setPeriod(r);
    } catch {
      toast.error("No se pudo abrir el comprobante");
    } finally {
      setOpening(false);
    }
  };

  const closeReceipt = () => {
    setEntry(null);
    setPeriod(null);
  };

  return (
    <div className="space-y-4">
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
            <Coins className="w-4 h-4 text-zinc-500" strokeWidth={1.75} />
            <div>
              <p className="text-sm font-semibold text-zinc-900">Salarios devengados</p>
              <p className="text-xs text-zinc-500">Comprobantes de pago de las quincenas aprobadas. Toca una fila para ver el comprobante.</p>
            </div>
          </div>

          {loading ? (
            <div className="px-5 py-10 text-center text-sm text-zinc-400" data-testid="salaries-loading">Cargando…</div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center" data-testid="salaries-empty">
              <Coins className="w-10 h-10 mx-auto mb-3 opacity-20 text-zinc-400" />
              <p className="text-sm text-zinc-500">Aún no hay salarios pagados para este trabajador.</p>
              <p className="text-xs text-zinc-400 mt-1">Los comprobantes aparecen aquí cuando se aprueba una quincena.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="salaries-table">
                <thead>
                  <tr className="text-zinc-500 uppercase tracking-wide text-[11px] border-b border-zinc-100">
                    <th className="text-left px-5 py-2.5 font-medium">Fecha de pago</th>
                    <th className="text-left px-3 py-2.5 font-medium">Nómina</th>
                    <th className="text-left px-3 py-2.5 font-medium">Rango de fechas</th>
                    <th className="text-right px-3 py-2.5 font-medium">Neto pagado</th>
                    <th className="text-right px-5 py-2.5 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.entry_id}
                      className="border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => openReceipt(r)}
                      data-testid={`salary-row-${r.period_id}`}
                    >
                      <td className="px-5 py-3 font-mono text-zinc-700">{formatDate(r.payment_date)}</td>
                      <td className="px-3 py-3 text-zinc-700">
                        <span className="font-medium">{r.period_label}</span>
                        {r.signed && (
                          <Badge className="ml-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Firmado</Badge>
                        )}
                      </td>
                      <td className="px-3 py-3 text-zinc-600 font-mono text-xs">
                        {formatDate(r.start_date)} → {formatDate(r.end_date)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-semibold text-zinc-900">{money(r.net_pay)}</td>
                      <td className="px-5 py-3 text-right">
                        <ChevronRight className="w-4 h-4 text-zinc-300 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Read-only receipt */}
      <Dialog open={!!entry} onOpenChange={(o) => !o && closeReceipt()}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">Comprobante de pago</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {entry?.employee_name} · {entry?.employee_code}
            </DialogDescription>
          </DialogHeader>
          {entry && period && (
            <div ref={receiptRef} className="p-5 bg-white text-zinc-950 text-[13px]" data-testid="salary-receipt-modal">
              {/* Header */}
              <div className="flex items-start justify-between border-b-2 border-zinc-900 pb-3 mb-4">
                <div>
                  <p className="text-base font-bold leading-tight">
                    {entry.company_legal_name || entry.company_trade_name || "—"}
                  </p>
                  <p className="text-[11px] text-zinc-500">Cédula jurídica: {entry.company_legal_id || "—"}</p>
                  {entry.company_address && (
                    <p className="text-[10px] text-zinc-400 leading-tight">{entry.company_address}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-400">Comprobante de pago</p>
                  <p className="font-mono text-xs text-zinc-600">#{entry.id?.slice(0, 8).toUpperCase()}</p>
                  <p className="text-[11px] text-zinc-500">{formatDate(period.start_date)} → {formatDate(period.end_date)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                <div><span className="text-zinc-500">Trabajador:</span> <strong>{entry.employee_name}</strong></div>
                <div><span className="text-zinc-500">Ocupación:</span> <strong>{entry.occupation || "—"}</strong></div>
              </div>

              {/* Detalle por día */}
              {(entry.daily_breakdown || []).length > 0 && (
                <div className="mb-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-700 border-b border-zinc-200 pb-1 mb-1">Detalle por día</p>
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-[11px]" data-testid="salary-receipt-daily">
                      <thead>
                        <tr className="text-zinc-500 uppercase tracking-wide text-[10px] border-b border-zinc-100">
                          <th className="text-left py-1 pr-2 font-medium">Fecha</th>
                          <th className="text-left py-1 px-2 font-medium">Día</th>
                          <th className="text-left py-1 px-2 font-medium">Estado</th>
                          <th className="text-right py-1 px-2 font-medium">Ord.</th>
                          <th className="text-right py-1 px-2 font-medium">Extra</th>
                          <th className="text-right py-1 px-2 font-medium">Pago día</th>
                          <th className="text-right py-1 pl-2 font-medium">Recargos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.daily_breakdown.map((d) => (
                          <tr
                            key={d.date}
                            className={`border-b border-zinc-50 ${
                              !d.is_business_day ? "bg-zinc-50/60" : d.is_holiday ? "bg-amber-50/40" : ""
                            }`}
                          >
                            <td className="py-1 pr-2 font-mono text-zinc-700">{formatDate(d.date)}</td>
                            <td className="py-1 px-2 text-zinc-600">{d.day_name}</td>
                            <td className="py-1 px-2 text-zinc-700">
                              {d.status}
                              {d.is_holiday && <span className="ml-1 text-[9px] text-amber-700 uppercase">FER</span>}
                            </td>
                            <td className="py-1 px-2 text-right font-mono text-zinc-700">{d.ordinary_hours ? d.ordinary_hours.toFixed(2) : "—"}</td>
                            <td className="py-1 px-2 text-right font-mono text-zinc-700">{d.overtime_hours ? d.overtime_hours.toFixed(2) : "—"}</td>
                            <td className="py-1 px-2 text-right font-mono">
                              {d.day_deduction > 0 ? (
                                <span className="text-red-600">{money(d.day_pay)}</span>
                              ) : d.day_pay > 0 ? money(d.day_pay) : "—"}
                            </td>
                            <td className="py-1 pl-2 text-right font-mono text-emerald-700">
                              {d.day_extra > 0 ? `+${money(d.day_extra)}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Totales del periodo */}
              <div className="mb-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 border-b border-zinc-200 pb-1 mb-1">Totales del periodo</p>
                <div className="divide-y divide-zinc-100">
                  {entry.payment_type === "monthly" && (
                    <Row label="Salario base (quincena)" value={money(entry.quincena_base)} />
                  )}
                  {entry.payment_type === "monthly" ? (
                    <Row label={`Horas ordinarias (${entry.ordinary_hours} h)`} value={money(entry.ordinary_pay)} />
                  ) : (
                    <Row
                      label={<span>Horas ordinarias <span className="text-zinc-400">({entry.ordinary_hours} h × {money(entry.hourly_rate)}/h)</span></span>}
                      value={money(entry.ordinary_pay)}
                    />
                  )}
                  <Row
                    label={<span>Horas extra <span className="text-zinc-400">({entry.overtime_hours} h × {money(entry.overtime_hourly_rate ?? (entry.hourly_rate || 0) * 1.5)}/h)</span></span>}
                    value={money(entry.overtime_pay)}
                  />
                  {entry.holiday_hours > 0 && (
                    <Row label={`Feriado (${entry.holiday_hours} h)`} value={money(entry.holiday_pay)} />
                  )}
                  {entry.incident_pay > 0 && (
                    <Row label={`Incidencias pagadas (${entry.incident_paid_pct_days} d equiv.)`} value={money(entry.incident_pay)} />
                  )}
                  <div className="flex items-center justify-between pt-1.5 font-bold">
                    <span className="text-sm">Salario bruto</span>
                    <span className="text-sm font-mono">{money(entry.gross_pay)}</span>
                  </div>
                </div>
              </div>

              {/* Deducciones */}
              <div className="mb-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-red-700 border-b border-zinc-200 pb-1 mb-1">Deducciones</p>
                <div className="divide-y divide-zinc-100">
                  <Row label={`CCSS obrero (${(entry.ccss_pct ?? 0).toString().replace(".", ",")}%)`} value={`-${money(entry.ccss_deduction)}`} neg />
                  {entry.income_tax > 0 && (
                    <Row label="Impuesto sobre la renta" value={`-${money(entry.income_tax)}`} neg />
                  )}
                  {entry.incident_unpaid_deduction > 0 && (
                    <Row label={`Incidencias no pagadas (${(entry.incident_unpaid_equivalent || 0).toFixed(2)} d equiv.)`} value={`-${money(entry.incident_unpaid_deduction)}`} neg />
                  )}
                  {(entry.vale_deductions || []).length > 0 && (
                    <div className="py-1.5">
                      <p className="text-[11px] text-zinc-600 font-medium">Adelantos de salario (VALES)</p>
                      {(entry.vale_deductions || []).map((vd) => (
                        <div key={vd.vale_id} className="pl-3 py-0.5 flex items-center justify-between text-[11px]">
                          <span className="text-zinc-500">
                            · {vd.mode === "single" ? "Pago único" : `Cuota ${(vd.installments_paid || 0) + 1}/${vd.installments}`}
                            {vd.reason ? ` — ${vd.reason}` : ""}
                          </span>
                          <span className="font-mono text-red-600">-{money(vd.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(entry.custom_deductions || []).length > 0 && (
                    <div className="py-1.5">
                      <p className="text-[11px] text-zinc-600 font-medium">Otras deducciones</p>
                      {(entry.custom_deductions || []).map((cd) => (
                        <div key={cd.id} className="pl-3 py-0.5 flex items-center justify-between text-[11px]">
                          <span className="text-zinc-600">· {cd.concept}{cd.notes ? <span className="text-zinc-400"> — {cd.notes}</span> : null}</span>
                          <span className="font-mono text-red-600">-{money(cd.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1.5 font-bold">
                    <span className="text-sm">Total deducciones</span>
                    <span className="text-sm font-mono text-red-700">
                      -{money((entry.ccss_deduction || 0) + (entry.income_tax || 0) + (entry.other_deductions || 0) + (entry.incident_unpaid_deduction || 0))}
                    </span>
                  </div>
                </div>
              </div>

              {/* Neto */}
              <div className="border-t-4 border-zinc-900 pt-3 flex items-center justify-between mb-4">
                <span className="text-base font-bold uppercase">Neto pagado</span>
                <span className="text-2xl font-bold font-mono" data-testid="salary-receipt-net">{money(entry.net_pay)}</span>
              </div>

              {/* Firma */}
              <div className="grid grid-cols-2 gap-6 mt-5">
                <div className="text-center">
                  {entry.signature ? (
                    <>
                      <SignatureImage src={entry.signature} height={70} />
                      <p className="text-[10px] text-zinc-600 mt-1"><strong>{entry.signature_name || entry.employee_name}</strong></p>
                      {entry.signature_id && <p className="text-[9px] text-zinc-500">Céd: {entry.signature_id}</p>}
                      <p className="text-[9px] text-zinc-400">Firmado {formatDate(entry.signed_at)}</p>
                    </>
                  ) : (
                    <div className="h-[70px] flex items-center justify-center border-b border-zinc-300">
                      <p className="text-[10px] text-zinc-400">Sin firma registrada</p>
                    </div>
                  )}
                  <p className="text-[9px] text-zinc-400 mt-1">Firma del trabajador</p>
                </div>
                <div className="text-center">
                  <div className="h-[70px] border-b border-zinc-300" />
                  <p className="text-[10px] text-zinc-600 mt-1"><strong>{period.approved_by || "RRHH"}</strong></p>
                  <p className="text-[9px] text-zinc-400">Autorizado por el patrono</p>
                </div>
              </div>

              <p className="text-[9px] text-zinc-400 text-center mt-4 italic">
                Tasa por hora: {money(entry.hourly_rate)} · Tasa diaria: {money(entry.daily_rate)} · VETCOMER Workforce System
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeReceipt} data-testid="salary-receipt-close">Cerrar</Button>
            <Button
              onClick={() => printElement(payslipRef.current, `Comprobante ${entry?.employee_code}`)}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="salary-receipt-print"
            >
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Nodo oculto: formato formal de impresión (una hoja carta) */}
      <div style={{ position: "fixed", left: "-10000px", top: 0, width: "816px" }} aria-hidden="true">
        <PayslipPrint ref={payslipRef} entry={entry} period={period} />
      </div>
    </div>
  );
}
